import React, { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { Plus, Search, Trash2, Edit2, CheckCircle, Clock, AlertCircle, XCircle, X } from 'lucide-react'
import { getTasks, createTasks, updateTask, deleteTask, getUsers, sendNotification } from '../../services/staffServices'
import { useStaffAuthStore } from '../../store/staffAuthStore'
import StaffLayout from './components/StaffLayout'
import toast from 'react-hot-toast'
import { StaggerContainer, StaggerItem, ModalOverlay, ButtonLoader } from './components/StaffAnimations'

const STATUS_COLORS = {
    PENDING: 'bg-amber-500/15 text-amber-400',
    IN_PROGRESS: 'bg-blue-500/15 text-blue-400',
    COMPLETED: 'bg-green-500/15 text-green-400',
    CANCELLED: 'bg-red-500/15 text-red-400',
}
const PRIORITY_COLORS = {
    LOW: 'bg-green-500/15 text-green-400',
    MEDIUM: 'bg-amber-500/15 text-amber-400',
    HIGH: 'bg-red-500/15 text-red-400',
    URGENT: 'bg-purple-500/15 text-purple-400',
}
const STATUS_CYCLE = { PENDING: 'IN_PROGRESS', IN_PROGRESS: 'COMPLETED', COMPLETED: 'PENDING', CANCELLED: 'PENDING' }
const STATUS_ICONS = { PENDING: AlertCircle, IN_PROGRESS: Clock, COMPLETED: CheckCircle, CANCELLED: XCircle }

const CATEGORIES = ['CONNECTIONS', 'RESTORATIONS', 'INVENTORY', 'LEADS']
const PRIORITIES = ['LOW', 'MEDIUM', 'HIGH', 'URGENT']
const STATUSES = ['PENDING', 'IN_PROGRESS', 'COMPLETED', 'CANCELLED']

// Avatar colors
const AVATAR_COLORS = ['bg-blue-600', 'bg-purple-600', 'bg-green-600', 'bg-amber-600', 'bg-rose-600', 'bg-indigo-600']

function UserAvatar({ user, size = 6 }) {
    const colorIdx = user?.name ? user.name.charCodeAt(0) % AVATAR_COLORS.length : 0
    const px = size * 4
    if (user?.profile_photo_url) {
        return <img src={user.profile_photo_url} alt={user.name} className="rounded-full object-cover shrink-0" style={{ width: px, height: px }} />
    }
    return (
        <div className={`rounded-full ${AVATAR_COLORS[colorIdx]} flex items-center justify-center text-[10px] font-bold text-white shrink-0`} style={{ width: px, height: px }}>
            {user?.name?.[0]?.toUpperCase() || '?'}
        </div>
    )
}

function addDays(days) {
    const d = new Date()
    d.setDate(d.getDate() + days)
    return d.toISOString().split('T')[0]
}

const DUE_DATE_SHORTCUTS = [
    { label: 'Today', value: () => addDays(0) },
    { label: 'Tomorrow', value: () => addDays(1) },
    { label: '+3 days', value: () => addDays(3) },
    { label: '+1 week', value: () => addDays(7) },
    { label: '+2 weeks', value: () => addDays(14) },
]

function TaskModal({ task, users, profile, onClose, onSave, isSaving }) {
    const isEdit = !!task?.id
    const [form, setForm] = useState({
        title: task?.title || '',
        description: task?.description || '',
        priority: task?.priority || 'MEDIUM',
        due_date: task?.due_date || addDays(1),
        status: task?.status || 'PENDING',
        task_category: task?.task_category || 'CONNECTIONS',
    })
    // Multi-assignee for create; single for edit
    const [selectedAssignees, setSelectedAssignees] = useState(
        isEdit ? [task.assigned_to].filter(Boolean) : []
    )

    const set = (k, v) => setForm(f => ({ ...f, [k]: v }))

    const toggleAssignee = (uid) => {
        setSelectedAssignees(prev =>
            prev.includes(uid) ? prev.filter(id => id !== uid) : [...prev, uid]
        )
    }

    const handleSubmit = (e) => {
        e.preventDefault()
        if (!form.title.trim()) { toast.error('Title is required'); return }
        if (!isEdit && selectedAssignees.length === 0) { toast.error('Select at least one assignee'); return }
        if (isEdit && selectedAssignees.length === 0) { toast.error('Select an assignee'); return }

        if (isEdit) {
            const assignee = users.find(u => u.id === selectedAssignees[0])
            onSave({
                ...form,
                assigned_to: selectedAssignees[0],
                assigned_to_name: assignee?.name || '',
            })
        } else {
            // Create one task per assignee
            const tasks = selectedAssignees.map(uid => {
                const assignee = users.find(u => u.id === uid)
                return {
                    ...form,
                    assigned_to: uid,
                    assigned_to_name: assignee?.name || '',
                    created_by: profile?.id,
                    created_by_name: profile?.name,
                }
            })
            onSave(tasks)
        }
    }

    return (
        <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4">
            <div className="bg-slate-900 rounded-2xl border border-white/8 w-full max-w-md max-h-[92vh] overflow-y-auto">
                <div className="flex items-center justify-between p-5 border-b border-white/5 sticky top-0 bg-slate-900 z-10">
                    <h2 className="font-semibold text-white">{isEdit ? 'Edit Task' : 'New Task'}</h2>
                    <button onClick={onClose} className="text-slate-400 hover:text-white transition"><X size={18} /></button>
                </div>
                <form onSubmit={handleSubmit} className="p-5 space-y-4">
                    {/* Title */}
                    <div>
                        <label className="text-xs text-slate-400 block mb-1">Title *</label>
                        <input
                            value={form.title}
                            onChange={e => set('title', e.target.value)}
                            className="w-full bg-slate-800 border border-white/8 rounded-xl px-3 py-2.5 text-sm text-white focus:outline-none focus:border-blue-500"
                            placeholder="Task title"
                        />
                    </div>

                    {/* Description */}
                    <div>
                        <label className="text-xs text-slate-400 block mb-1">Description</label>
                        <textarea
                            value={form.description}
                            onChange={e => set('description', e.target.value)}
                            rows={2}
                            className="w-full bg-slate-800 border border-white/8 rounded-xl px-3 py-2.5 text-sm text-white focus:outline-none focus:border-blue-500 resize-none"
                            placeholder="Task details..."
                        />
                    </div>

                    {/* Assignees */}
                    <div>
                        <label className="text-xs text-slate-400 block mb-1">
                            {isEdit ? 'Assign To *' : 'Assign To * (multi-select → one task each)'}
                        </label>
                        <div className="bg-slate-800 border border-white/8 rounded-xl p-2 max-h-36 overflow-y-auto space-y-0.5">
                            {users.map(u => {
                                const selected = selectedAssignees.includes(u.id)
                                return (
                                    <button
                                        key={u.id}
                                        type="button"
                                        onClick={() => {
                                            if (isEdit) setSelectedAssignees([u.id])
                                            else toggleAssignee(u.id)
                                        }}
                                        className={`w-full flex items-center gap-2 px-2 py-1.5 rounded-lg text-sm transition ${selected ? 'bg-blue-600/25 text-blue-300' : 'text-slate-300 hover:bg-white/5'}`}
                                    >
                                        <UserAvatar user={u} size={6} />
                                        <span className="flex-1 text-left">{u.name}</span>
                                        {selected && <CheckCircle size={13} className="text-blue-400 shrink-0" />}
                                    </button>
                                )
                            })}
                        </div>
                        {!isEdit && selectedAssignees.length > 0 && (
                            <p className="text-xs text-blue-400 mt-1">{selectedAssignees.length} assignee{selectedAssignees.length > 1 ? 's' : ''} → {selectedAssignees.length} task{selectedAssignees.length > 1 ? 's' : ''} will be created</p>
                        )}
                    </div>

                    {/* Category + Priority */}
                    <div className="grid grid-cols-2 gap-3">
                        <div>
                            <label className="text-xs text-slate-400 block mb-1">Category</label>
                            <select
                                value={form.task_category}
                                onChange={e => set('task_category', e.target.value)}
                                className="w-full bg-slate-800 border border-white/8 rounded-xl px-3 py-2.5 text-sm text-white focus:outline-none focus:border-blue-500"
                            >
                                {CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
                            </select>
                        </div>
                        <div>
                            <label className="text-xs text-slate-400 block mb-1">Priority</label>
                            <select
                                value={form.priority}
                                onChange={e => set('priority', e.target.value)}
                                className="w-full bg-slate-800 border border-white/8 rounded-xl px-3 py-2.5 text-sm text-white focus:outline-none focus:border-blue-500"
                            >
                                {PRIORITIES.map(p => <option key={p} value={p}>{p}</option>)}
                            </select>
                        </div>
                    </div>

                    {/* Due date shortcuts */}
                    <div>
                        <label className="text-xs text-slate-400 block mb-1">Due Date</label>
                        <div className="flex flex-wrap gap-1.5 mb-2">
                            {DUE_DATE_SHORTCUTS.map(s => (
                                <button
                                    key={s.label}
                                    type="button"
                                    onClick={() => set('due_date', s.value())}
                                    className={`px-2.5 py-1 text-xs rounded-lg border transition ${form.due_date === s.value()
                                        ? 'bg-blue-600 border-blue-500 text-white'
                                        : 'border-white/10 text-slate-400 hover:text-white hover:border-white/20'
                                        }`}
                                >
                                    {s.label}
                                </button>
                            ))}
                        </div>
                        <input
                            type="date"
                            value={form.due_date}
                            onChange={e => set('due_date', e.target.value)}
                            className="w-full bg-slate-800 border border-white/8 rounded-xl px-3 py-2.5 text-sm text-white focus:outline-none focus:border-blue-500"
                        />
                    </div>

                    {/* Initial status (edit only shows full list) */}
                    <div>
                        <label className="text-xs text-slate-400 block mb-1">Initial Status</label>
                        <div className="flex gap-2">
                            {(isEdit ? STATUSES : ['PENDING', 'IN_PROGRESS']).map(s => (
                                <button
                                    key={s}
                                    type="button"
                                    onClick={() => set('status', s)}
                                    className={`flex-1 py-2 rounded-xl text-xs font-medium transition border ${form.status === s
                                        ? 'bg-blue-600 border-blue-500 text-white'
                                        : 'border-white/8 text-slate-400 hover:text-white'
                                        }`}
                                >
                                    {s.replace('_', ' ')}
                                </button>
                            ))}
                        </div>
                    </div>

                    <div className="flex gap-3 pt-2">
                        <button type="button" onClick={onClose} className="flex-1 bg-slate-800 hover:bg-slate-700 text-slate-300 py-2.5 rounded-xl text-sm transition">
                            Cancel
                        </button>
                        <button type="submit" disabled={isSaving} className="flex-1 bg-blue-600 hover:bg-blue-500 disabled:opacity-50 text-white py-2.5 rounded-xl text-sm font-medium transition">
                            <ButtonLoader loading={isSaving}>{isEdit ? 'Update' : `Create${selectedAssignees.length > 1 ? ` (${selectedAssignees.length})` : ''}`}</ButtonLoader>
                        </button>
                    </div>
                </form>
            </div>
        </div>
    )
}

export default function Tasks() {
    const qc = useQueryClient()
    const { profile } = useStaffAuthStore()
    const [search, setSearch] = useState('')
    const [statusFilter, setStatusFilter] = useState('ALL')
    const [categoryFilter, setCategoryFilter] = useState('ALL')
    const [sortAsc, setSortAsc] = useState(false)
    const [modal, setModal] = useState(null)

    const { data: tasks = [], isLoading } = useQuery({ queryKey: ['tasks'], queryFn: () => getTasks() })
    const { data: users = [] } = useQuery({ queryKey: ['users'], queryFn: getUsers })

    const createMut = useMutation({
        mutationFn: createTasks,
        onSuccess: async (created) => {
            qc.invalidateQueries(['tasks'])
            setModal(null)
            toast.success(created.length > 1 ? `${created.length} tasks created` : 'Task created')
            // Fire push notifications to each assignee
            for (const task of created) {
                try {
                    await sendNotification({
                        type: 'task_assigned',
                        user_id: task.assigned_to,
                        data: {
                            task_title: task.title,
                            task_description: task.description || '',
                        },
                    })
                } catch (err) {
                    // non-blocking — log silently
                    console.warn('Notification failed for', task.assigned_to, err)
                }
            }
        },
        onError: e => toast.error(e.message),
    })
    const updateMut = useMutation({
        mutationFn: ({ id, ...rest }) => updateTask(id, rest),
        onSuccess: () => { qc.invalidateQueries(['tasks']); setModal(null); toast.success('Task updated') },
        onError: e => toast.error(e.message),
    })
    const deleteMut = useMutation({
        mutationFn: deleteTask,
        onSuccess: () => { qc.invalidateQueries(['tasks']); toast.success('Task deleted') },
        onError: e => toast.error(e.message),
    })
    const cycleMut = useMutation({
        mutationFn: ({ id, status }) => updateTask(id, { status }),
        onSuccess: () => qc.invalidateQueries(['tasks']),
        onError: e => toast.error(e.message),
    })

    const handleSave = (data) => {
        if (modal === 'new') {
            createMut.mutate(Array.isArray(data) ? data : [data])
        } else {
            updateMut.mutate({ id: modal.id, ...data })
        }
    }

    const filtered = tasks
        .filter(t => {
            const matchSearch = !search ||
                t.title?.toLowerCase().includes(search.toLowerCase()) ||
                t.assigned_to_name?.toLowerCase().includes(search.toLowerCase())
            const matchStatus = statusFilter === 'ALL' || t.status === statusFilter
            const matchCat = categoryFilter === 'ALL' || t.task_category === categoryFilter
            return matchSearch && matchStatus && matchCat
        })
        .sort((a, b) => {
            const da = new Date(a.created_at), db = new Date(b.created_at)
            return sortAsc ? da - db : db - da
        })

    const statusCounts = STATUSES.reduce((acc, s) => ({ ...acc, [s]: tasks.filter(t => t.status === s).length }), {})

    return (
        <StaffLayout>
            <div className="space-y-5">
                {/* Header */}
                <div className="flex items-center justify-between">
                    <h1 className="text-xl font-bold text-white">
                        Tasks
                        <span className="text-slate-500 font-normal text-base ml-2">({tasks.length})</span>
                    </h1>
                    <button
                        onClick={() => setModal('new')}
                        className="flex items-center gap-2 bg-blue-600 hover:bg-blue-500 text-white px-4 py-2 rounded-xl text-sm font-medium transition"
                    >
                        <Plus size={16} /> New Task
                    </button>
                </div>

                {/* Status summary chips */}
                <div className="flex flex-wrap gap-2">
                    <button
                        onClick={() => setStatusFilter('ALL')}
                        className={`px-3 py-1.5 rounded-xl text-xs font-medium transition ${statusFilter === 'ALL' ? 'bg-blue-600 text-white' : 'bg-slate-900 border border-white/5 text-slate-400 hover:text-white'}`}
                    >
                        All ({tasks.length})
                    </button>
                    {STATUSES.map(s => (
                        <button
                            key={s}
                            onClick={() => setStatusFilter(statusFilter === s ? 'ALL' : s)}
                            className={`px-3 py-1.5 rounded-xl text-xs font-medium transition ${statusFilter === s ? 'bg-blue-600 text-white' : `${STATUS_COLORS[s]} border border-white/5 hover:opacity-80`}`}
                        >
                            {s.replace('_', ' ')} ({statusCounts[s] || 0})
                        </button>
                    ))}
                </div>

                {/* Search + Category filter + Sort */}
                <div className="flex flex-wrap gap-3">
                    <div className="relative flex-1 min-w-[180px]">
                        <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                        <input
                            value={search}
                            onChange={e => setSearch(e.target.value)}
                            placeholder="Search tasks or assignee..."
                            className="w-full bg-slate-900 border border-white/5 rounded-xl pl-9 pr-4 py-2.5 text-sm text-white placeholder-slate-500 focus:outline-none focus:border-blue-500"
                        />
                    </div>
                    <select
                        value={categoryFilter}
                        onChange={e => setCategoryFilter(e.target.value)}
                        className="bg-slate-900 border border-white/5 rounded-xl px-3 py-2.5 text-sm text-white focus:outline-none focus:border-blue-500"
                    >
                        <option value="ALL">All Categories</option>
                        {CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
                    </select>
                    <button
                        onClick={() => setSortAsc(!sortAsc)}
                        className="bg-slate-900 border border-white/5 rounded-xl px-3 py-2.5 text-sm text-slate-400 hover:text-white transition"
                    >
                        {sortAsc ? '↑ Oldest' : '↓ Newest'}
                    </button>
                </div>

                {/* Task cards */}
                {isLoading ? (
                    <div className="space-y-3">
                        {Array(4).fill(0).map((_, i) => (
                            <div key={i} className="bg-slate-900 rounded-xl border border-white/5 p-4 animate-pulse h-24" />
                        ))}
                    </div>
                ) : filtered.length === 0 ? (
                    <div className="bg-slate-900 rounded-xl border border-white/5 p-12 text-center text-slate-500">
                        No tasks found
                    </div>
                ) : (
                    <StaggerContainer className="space-y-3">
                        {filtered.map(task => {
                            const StatusIcon = STATUS_ICONS[task.status] || AlertCircle
                            const assigneeUser = users.find(u => u.id === task.assigned_to)
                            const isOverdue = task.due_date && new Date(task.due_date) < new Date() && task.status !== 'COMPLETED'

                            return (
                                <StaggerItem key={task.id}>
                                <div className="bg-slate-900 rounded-xl border border-white/5 hover:border-white/20 hover:shadow-lg hover:shadow-black/10 p-4 transition-all duration-200">
                                    <div className="flex items-start gap-3">
                                        {/* Status cycle button */}
                                        <button
                                            onClick={() => cycleMut.mutate({ id: task.id, status: STATUS_CYCLE[task.status] || 'PENDING' })}
                                            className={`mt-0.5 p-1.5 rounded-lg ${STATUS_COLORS[task.status]} hover:opacity-80 transition shrink-0`}
                                            title={`Click to → ${STATUS_CYCLE[task.status]}`}
                                        >
                                            <StatusIcon size={14} />
                                        </button>

                                        <div className="flex-1 min-w-0">
                                            <div className="flex items-start justify-between gap-2">
                                                <div className="min-w-0">
                                                    <p className={`font-medium text-sm ${task.status === 'COMPLETED' ? 'line-through text-slate-500' : 'text-white'}`}>
                                                        {task.title}
                                                    </p>
                                                    {task.description && (
                                                        <p className="text-xs text-slate-500 mt-0.5 truncate">{task.description}</p>
                                                    )}
                                                </div>
                                                <div className="flex gap-1.5 shrink-0">
                                                    <button onClick={() => setModal(task)} className="text-slate-500 hover:text-blue-400 transition p-1">
                                                        <Edit2 size={13} />
                                                    </button>
                                                    <button
                                                        onClick={() => { if (window.confirm('Delete task?')) deleteMut.mutate(task.id) }}
                                                        className="text-slate-500 hover:text-red-400 transition p-1"
                                                    >
                                                        <Trash2 size={13} />
                                                    </button>
                                                </div>
                                            </div>

                                            {/* Meta row */}
                                            <div className="flex flex-wrap items-center gap-2 mt-2">
                                                {/* Assignee */}
                                                <div className="flex items-center gap-1.5">
                                                    <UserAvatar user={assigneeUser} size={5} />
                                                    <span className="text-xs text-slate-400">{task.assigned_to_name || '—'}</span>
                                                </div>

                                                {/* Category */}
                                                <span className="bg-slate-800 text-slate-400 text-[10px] px-2 py-0.5 rounded-lg">
                                                    {task.task_category}
                                                </span>

                                                {/* Priority */}
                                                <span className={`text-[10px] px-2 py-0.5 rounded-lg font-medium ${PRIORITY_COLORS[task.priority]}`}>
                                                    {task.priority}
                                                </span>

                                                {/* Due date */}
                                                {task.due_date && (
                                                    <span className={`text-[10px] px-2 py-0.5 rounded-lg ${isOverdue ? 'bg-red-500/15 text-red-400' : 'bg-slate-800 text-slate-500'}`}>
                                                        Due {new Date(task.due_date).toLocaleDateString('en-IN', { day: 'numeric', month: 'short' })}
                                                    </span>
                                                )}

                                                {/* Progress if available */}
                                                {task.progress_percentage != null && task.progress_percentage > 0 && (
                                                    <span className="text-[10px] text-blue-400">{task.progress_percentage}%</span>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                </div>
                                </StaggerItem>
                            )
                        })}
                    </StaggerContainer>
                )}
            </div>

            <ModalOverlay show={!!modal}>
                <TaskModal
                    task={modal === 'new' ? null : modal}
                    users={users}
                    profile={profile}
                    onClose={() => setModal(null)}
                    onSave={handleSave}
                    isSaving={createMut.isLoading || updateMut.isLoading}
                />
            </ModalOverlay>
        </StaffLayout>
    )
}
